/**
 * URL utility functions
 */
import type React from 'react';

/**
 * Joins a base URL with a path, ensuring there's exactly one slash between them.
 * Handles cases where baseUrl ends with '/' and/or path starts with '/'.
 *
 * @param baseUrl - Base URL (e.g., 'https://example.com' or 'https://example.com/')
 * @param path - Path to append (e.g., 'Images/user.png' or '/Images/user.png')
 * @returns Properly joined URL
 *
 * @example
 * joinUrl('https://example.com', '/Images/user.png')     // 'https://example.com/Images/user.png'
 * joinUrl('https://example.com/', '/Images/user.png')    // 'https://example.com/Images/user.png'
 * joinUrl('https://example.com/', 'Images/user.png')     // 'https://example.com/Images/user.png'
 * joinUrl('https://example.com', 'Images/user.png')      // 'https://example.com/Images/user.png'
 */
export function joinUrl(baseUrl: string, path: string): string
{
  if (!baseUrl) return path;
  if (!path) return baseUrl;

  // Remove trailing slash from base
  const normalizedBase = baseUrl.replace(/\/$/, '');

  // Ensure path starts with slash
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  return normalizedBase + normalizedPath;
}

/**
 * Ensures a URL ends with a trailing slash
 */
export function ensureTrailingSlash(url: string): string
{
  return url.endsWith('/') ? url : `${url}/`;
}

/**
 * Removes trailing slash from a URL
 */
export function removeTrailingSlash(url: string): string
{
  return url.replace(/\/$/, '');
}

/**
 * Handles clicks on `a.originalConversationLink` elements rendered inside HTML bodies.
 * Calls onOpen with the linked conversation ID so the caller can open the InfoWindow.
 * Returns true if the click was handled (caller should stop further processing).
 */
export function handleOriginalConversationLinkClick(
  e: React.MouseEvent | MouseEvent,
  onOpen: (conversationId: number) => void,
): boolean
{
  const target = (e.target as HTMLElement).closest('a.originalConversationLink');
  if (!target) return false;

  e.preventDefault();
  e.stopPropagation();

  const linkedId = (target as HTMLElement).dataset.linkedConversationid;
  if (linkedId)
  {
    onOpen(parseInt(linkedId, 10));
  }
  return true;
}

/**
 * Handles clicks on the `[[Card - Project]]` links inserted by the `[[` autocomplete.
 * Delegated on the body container, since re-rendering the HTML body replaces the anchors.
 * Returns true if the click was handled (caller should stop further processing).
 */
export function handleLinkedCardClick(
  e: React.MouseEvent | MouseEvent,
  onOpen: (conversationId: number) => void,
): boolean
{
  const target = (e.target as HTMLElement).closest('a[link-conversation-id]');
  if (!target) return false;

  e.preventDefault();
  e.stopPropagation();

  const linkedId = Number(target.getAttribute('link-conversation-id'));
  if (linkedId && !Number.isNaN(linkedId))
  {
    onOpen(linkedId);
  }
  return true;
}
